import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Sticky from 'react-sticky-el';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { UserSessionContext } from 'components/UserSession';
import SearchResultContainer from './SearchResultContainer';

const RootContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const StyledSectionTitle = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 14px;
  color: #999;
  padding: 16px 0;
  background-color: #F0F2F5;
`;

const AllSearchResultContainer = ({
  showResult,
  exhibitors,
  webinars,
  participants,
  products,
  speakers,
  scrollableTarget,
}) => {
  const { t } = useTranslation();
  const { canSee } = useContext(UserSessionContext);
  const locale = useSelector(selectLocale);
  const { pageNames, features } = useSelector((s) => s.eventConfigs);

  return (
    <RootContainer>
      {
        showResult && (
          <>
            {
              exhibitors.length > 0 && (
                <>
                  <Sticky
                    scrollElement={scrollableTarget}
                    stickyStyle={{ zIndex: 10 }}
                  >
                    <StyledSectionTitle>
                      {pageNames.exhibitors?.[locale] || t('page.exhibitors', 'Exhibitors')}
                    </StyledSectionTitle>
                  </Sticky>
                  <SearchResultContainer
                    type="exhibitor"
                    results={exhibitors}
                  />
                </>
              )
            }
            {
              webinars.length > 0 && (
                <>
                  <Sticky
                    scrollElement={scrollableTarget}
                    stickyStyle={{ zIndex: 10 }}
                  >
                    <StyledSectionTitle>
                      {pageNames.webinar?.[locale] || t('page.webinar', 'Webinars')}
                    </StyledSectionTitle>
                  </Sticky>
                  <SearchResultContainer
                    type="webinar"
                    results={webinars}
                  />
                </>
              )
            }
            {
              canSee('webinar') && speakers.length > 0 && (
                <>
                  <Sticky
                    scrollElement={scrollableTarget}
                    stickyStyle={{ zIndex: 10 }}
                  >
                    <StyledSectionTitle>
                      {pageNames.speakers?.[locale] || t('page.speakers', 'Speakers')}
                    </StyledSectionTitle>
                  </Sticky>
                  <SearchResultContainer
                    type="speakers"
                    results={speakers}
                  />
                </>
              )
            }
            {
              canSee('participant') && participants.length > 0 && (
                <>
                  <Sticky
                    scrollElement={scrollableTarget}
                    stickyStyle={{ zIndex: 10 }}
                  >
                    <StyledSectionTitle>
                      {pageNames.participant?.[locale] || t('page.participants', 'Participants')}
                    </StyledSectionTitle>
                  </Sticky>
                  <SearchResultContainer
                    type="participant"
                    results={participants}
                  />
                </>
              )
            }
            {
              features.includes('productPage') && products.length > 0 && (
                <>
                  <Sticky
                    scrollElement={scrollableTarget}
                    stickyStyle={{ zIndex: 10 }}
                  >
                    <StyledSectionTitle>
                      {pageNames.products?.[locale] || t('page.products', 'Products')}
                    </StyledSectionTitle>
                  </Sticky>
                  <SearchResultContainer
                    type="products"
                    results={products}
                  />
                </>
              )
            }

          </>
        )
      }
    </RootContainer>
  );
};

AllSearchResultContainer.propTypes = {
  showResult: PropTypes.bool,
  exhibitors: PropTypes.arrayOf(PropTypes.shape()),
  webinars: PropTypes.arrayOf(PropTypes.shape()),
  participants: PropTypes.arrayOf(PropTypes.shape()),
  products: PropTypes.arrayOf(PropTypes.shape()),
  speakers: PropTypes.arrayOf(PropTypes.shape()),
  scrollableTarget: PropTypes.instanceOf(Element),
};

AllSearchResultContainer.defaultProps = {
  showResult: false,
  exhibitors: [],
  webinars: [],
  participants: [],
  products: [],
  speakers: [],
  scrollableTarget: document.body,
};

export default AllSearchResultContainer;
